<template>
  <div v-if="show" class="modal-overlay" @click.self="attemptClose">
    <!-- Modal -->
    <div class="modal-content modal-content-extra-large max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true"
      aria-labelledby="sync-modal-title">
      <!-- Header -->
      <div class="modal-header">
        <div>
          <h2 id="sync-modal-title" class="text-2xl font-bold text-gray-900">Sync Meals Shopping List</h2>
          <!-- Step indicator -->
          <div class="flex items-center mt-3">
            <template v-for="step in totalSteps" :key="step">
              <!-- Step circle + label -->
              <div class="flex flex-col items-center">
                <div :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all',
                  currentStep >= step ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-600'
                ]">
                  {{ step }}
                </div>
                <span class="text-xs mt-1 whitespace-nowrap" :class="currentStep >= step ? 'text-primary-600 font-medium' : 'text-gray-500'">
                  {{ stepLabels[step - 1] }}
                </span>
              </div>
              <!-- Connector between steps -->
              <div v-if="step < totalSteps" :class="[
                'w-10 h-0.5 mb-4 transition-all',
                currentStep > step ? 'bg-primary-500' : 'bg-gray-200'
              ]"></div>
            </template>
          </div>
        </div>
        <button @click="attemptClose" class="modal-close-button" aria-label="Close modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <!-- Step 1: Basic Information -->
        <ShoppingListBasicForm v-if="currentStep === 1" v-model="formData" :errors="formErrors"
          :submit-attempted="submitAttempted" :show-sync-explanation="true"
          @submit="handleStep1Next" @cancel="attemptClose" />

        <!-- Step 2: Review & Select Meals -->
        <MealsReviewStep v-if="currentStep === 2" :meals="meals" :loading="loadingMeals" :date-range="dateRange"
          @previous="currentStep = 1" @next="handleStep2Next" />

        <!-- Step 3: Review & Select Ingredients -->
        <IngredientsReviewStep v-if="currentStep === 3" :ingredients="ingredients"
          :loading="loadingIngredients || syncingShoppingList" @previous="currentStep = 2"
          @next="handleStep3Next" @confirm="handleStep3Confirm" />

        <!-- Step 4: Resolve Conflicts -->
        <ConflictResolutionStep v-if="currentStep === 4" :conflicts="conflictGroups"
          @previous="currentStep = 3" @next="handleStep4Next" />
      </div>

    </div>
  </div>

  <!-- Unsaved Changes Dialog -->
  <UnsavedChangesDialog
    :show="showUnsavedChangesDialog"
    @keep-editing="keepEditing"
    @discard="discardChanges"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useMutation, useApolloClient } from '@vue/apollo-composable'
import { SYNC_SHOPPING_LIST, CREATE_SHOPPING_ITEM } from '../../graphql/mutations'
import { GET_MEALS_BY_DATE_RANGE, FIND_INGREDIENTS_BY_DATE_RANGE } from '../../graphql/queries'
import { showError } from '../../utils/errorHandling'
import { vm } from '../../utils/validationMessages'
import ShoppingListBasicForm from './ShoppingListBasicForm.vue'
import MealsReviewStep from './MealsReviewStep.vue'
import IngredientsReviewStep from './IngredientsReviewStep.vue'
import ConflictResolutionStep, { type ConflictGroup, type MergedItem } from './ConflictResolutionStep.vue'
import UnsavedChangesDialog from '../UnsavedChangesDialog.vue'
import { useBodyScrollLock } from '../../composables/useBodyScrollLock'

import dayjs from 'dayjs'

// Props
interface Props {
  show: boolean
}

const props = defineProps<Props>()
useBodyScrollLock(() => props.show)

// Emits
const emit = defineEmits<{
  close: []
  created: [shoppingList: any]
}>()

const stepLabels = computed(() =>
  conflictGroups.value.length > 0
    ? ['Basic Info', 'Review Meals', 'Review Ingredients', 'Resolve Conflicts']
    : ['Basic Info', 'Review Meals', 'Review Ingredients']
)
const totalSteps = computed(() => stepLabels.value.length)

// State
const currentStep = ref(1)
const formData = ref({
  title: '',
  start_date: '',
  end_date: '',
})

const formErrors = ref({
  title: '',
  start_date: '',
  end_date: '',
})

const dateRange = ref({
  start_date: '',
  end_date: ''
})

const loadingMeals = ref(false)
const loadingIngredients = ref(false)
const meals = ref([] as any[])
const ingredients = ref([] as any[])
const excludedMealIds = ref([] as string[])
const excludedIngredientIds = ref([] as string[])
const conflictGroups = ref<ConflictGroup[]>([])
const showUnsavedChangesDialog = ref(false)
const submitAttempted = ref(false)

// Apollo client
const { client } = useApolloClient()

// Mutations
const { mutate: syncShoppingList, loading: syncingShoppingList } = useMutation(SYNC_SHOPPING_LIST)
const { mutate: createShoppingItem } = useMutation(CREATE_SHOPPING_ITEM)

// Computed
const hasUnsavedChanges = computed(() => {
  return formData.value.title.trim() !== '' ||
         formData.value.start_date !== '' ||
         formData.value.end_date !== '' ||
         currentStep.value > 1
})

// Methods
const resetForm = () => {
  formData.value = { title: '', start_date: '', end_date: '' }
  formErrors.value = { title: '', start_date: '', end_date: '' }
  submitAttempted.value = false
  currentStep.value = 1
  dateRange.value = { start_date: '', end_date: '' }
  meals.value = []
  ingredients.value = []
  excludedMealIds.value = []
  excludedIngredientIds.value = []
  conflictGroups.value = []
}

const validateStep1 = () => {
  formErrors.value = { title: '', start_date: '', end_date: '' }
  let isValid = true

  if (!formData.value.title.trim()) {
    formErrors.value.title = vm.shoppingListName.required
    isValid = false
  }

  if (!formData.value.start_date) {
    formErrors.value.start_date = vm.startDate.required
    isValid = false
  }

  if (!formData.value.end_date) {
    formErrors.value.end_date = vm.endDate.required
    isValid = false
  }

  if (formData.value.start_date && formData.value.end_date) {
    if (dayjs(formData.value.end_date).isBefore(dayjs(formData.value.start_date))) {
      formErrors.value.end_date = vm.endDate.afterStart
      isValid = false
    }
  }

  return isValid
}

const fetchMeals = async () => {
  loadingMeals.value = true
  try {
    const result = await client.query({
      query: GET_MEALS_BY_DATE_RANGE,
      variables: {
        start_date: dateRange.value.start_date,
        end_date: dateRange.value.end_date,
        show_active: true
      },
      fetchPolicy: 'network-only'
    })
    meals.value = result.data?.mealsByDateRange || []
  } catch (error) {
    showError(error, 'fetch meals')
    meals.value = []
  } finally {
    loadingMeals.value = false
  }
}

const fetchIngredients = async () => {
  loadingIngredients.value = true
  try {
    const result = await client.query({
      query: FIND_INGREDIENTS_BY_DATE_RANGE,
      variables: {
        start_date: dateRange.value.start_date,
        end_date: dateRange.value.end_date
      },
      fetchPolicy: 'no-cache'
    })
    ingredients.value = result.data?.findIngredientsByDateRange || []
  } catch (error) {
    showError(error, 'fetch ingredients')
    ingredients.value = []
  } finally {
    loadingIngredients.value = false
  }
}

const handleStep1Next = async () => {
  submitAttempted.value = true
  if (!validateStep1()) {
    return
  }

  dateRange.value = {
    start_date: dayjs(formData.value.start_date).format('DD/MM/YYYY'),
    end_date: dayjs(formData.value.end_date).format('DD/MM/YYYY')
  }

  currentStep.value = 2
  await fetchMeals()
}

const handleStep3Confirm = async (excluded: string[]) => {
  excludedIngredientIds.value = excluded
  await createList([], [])
}

const handleStep2Next = async (excluded: string[]) => {
  excludedMealIds.value = excluded
  currentStep.value = 3
  await fetchIngredients()
}

const detectConflicts = (included: any[]): ConflictGroup[] => {
  const map = new Map<string, any[]>()
  for (const item of included) {
    const key = item.name.toLowerCase().trim()
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(item)
  }
  return Array.from(map.values())
    .filter(items => items.length > 1)
    .map(items => ({ name: items[0].name, items }))
}

const handleStep3Next = async (excluded: string[]) => {
  excludedIngredientIds.value = excluded

  // Find conflicts among the included ingredients
  const included = ingredients.value.filter(i => !excluded.includes(i.id))
  conflictGroups.value = detectConflicts(included)

  if (conflictGroups.value.length > 0) {
    // Go to conflict resolution step
    currentStep.value = 4
  } else {
    // No conflicts — create the list directly
    await createList([], [])
  }
}

const handleStep4Next = async (additionalExcluded: string[], mergedItems: MergedItem[]) => {
  await createList(additionalExcluded, mergedItems)
}

const createList = async (additionalExcluded: string[], mergedItems: MergedItem[]) => {
  try {
    const result = await syncShoppingList({
      title: formData.value.title,
      start_date: dayjs(formData.value.start_date).format('DD/MM/YYYY'),
      end_date: dayjs(formData.value.end_date).format('DD/MM/YYYY'),
      excluded_meal_ids: excludedMealIds.value,
      excluded_ingredient_ids: [...excludedIngredientIds.value, ...additionalExcluded],
    })

    const newList = result?.data?.syncShoppingList
    const listId = newList?.id

    // Create merged items on top of the synced list
    if (listId && mergedItems.length > 0) {
      await Promise.allSettled(
        mergedItems.map((item, idx) =>
          createShoppingItem({
            list_id: listId,
            name: item.name,
            ingredient_id: item.ingredient_id,
            quantity: item.quantity,
            unit: item.unit,
            price: item.price,
            type: item.group?.name ?? 'Other',
            position: idx,
          })
        )
      )
    }

    emit('created', newList)
    closeModal()
  } catch (error) {
    showError(error, 'sync shopping list')
  }
}

const attemptClose = () => {
  if (hasUnsavedChanges.value) {
    showUnsavedChangesDialog.value = true
  } else {
    closeModal()
  }
}

const closeModal = () => {
  resetForm()
  showUnsavedChangesDialog.value = false
  emit('close')
}

const keepEditing = () => {
  showUnsavedChangesDialog.value = false
}

const discardChanges = () => {
  closeModal()
}

// Watch for modal visibility changes
watch(() => props.show, (show) => {
  if (!show) {
    resetForm()
  }
})
</script>
